/**
 * <AddMovementDialog> — cash_drop or float_adjust.
 */
import { useState } from "react";
import { toast } from "sonner";
import { OperationalDrawer, BottomActionBar } from "@/components/operational";
import { ArrowRightLeft } from "lucide-react";
import { foApi , formatApiError } from "../foApi";
import { NumberInput } from "@/components/ui/number-input";

const LABELS = {
  cash_drop: { title: "Cash Drop", helper: "Drop cash into the safe / bank" },
  float_adjust: { title: "Float Adjust", helper: "Reason-required adjustment" },
};

export default function AddMovementDialog({ open, onOpenChange, shiftId, kind, onSuccess }) {
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const meta = LABELS[kind] || LABELS.cash_drop;

  const submit = async () => {
    const minor = Math.round(parseFloat(amount || 0) * 100);
    if (!minor || minor <= 0) { toast.error("Enter a positive amount"); return; }
    setSaving(true);
    try {
      await foApi.addMovement(shiftId, {
        kind,
        amount_minor: minor,
        reason: reason || null,
      });
      setAmount(""); setReason("");
      onSuccess?.();
    } catch (e) {
      toast.error(formatApiError(e, "Failed to record movement"));
    } finally { setSaving(false); }
  };

  return (
    <OperationalDrawer open={open} onOpenChange={onOpenChange}
      title={meta.title} subtitle={meta.helper}
      size="sm" testid={`movement-${kind}-drawer`}
      footer={
        <BottomActionBar
          primary={{ label: saving ? "Recording..." : "Record",
                       icon: ArrowRightLeft, tone: "primary", onClick: submit,
                       loading: saving, disabled: !amount,
                       testid: "movement-confirm" }}
          secondaryActions={[
            { label: "Cancel", tone: "secondary", onClick: () => onOpenChange(false), testid: "movement-cancel" }
          ]}
        />
      }>
      <div className="p-4 space-y-4">
        <div>
          <label className="text-[12px] uppercase tracking-wider text-slate-500 font-semibold">Amount (₹)</label>
          <NumberInput min={0} step="0.01" value={amount}
                   onChange={e => setAmount(e.target.value)}
                   className="w-full mt-1 border-2 border-slate-300 rounded-lg p-3 text-[18px] font-semibold h-14"
                   placeholder="0.00"
                   data-testid="movement-amount"/>
        </div>
        <div>
          <label className="text-[12px] uppercase tracking-wider text-slate-500 font-semibold">Reason</label>
          <textarea value={reason} onChange={e => setReason(e.target.value)}
                      className="w-full mt-1 border-2 border-slate-300 rounded-lg p-2 text-[14px] min-h-[80px]"
                      data-testid="movement-reason"/>
        </div>
      </div>
    </OperationalDrawer>
  );
}
